import { Component, computed, effect, inject, input, signal, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Conversation, ConversationSource, PrivacyLevel } from '@vendasta/meeting-analysis';
import { SourcePipe } from './source/source.pipe';
import { StartAndEndComponent } from '../../meeting-page/meeting-page-v2/start-and-end/start-and-end.component';
import { GuestsComponent } from '../../meeting-page/guests/guests.component';
import { ConversationPagePathPipe } from '@galaxy/crm/static';
import { MatDialog } from '@angular/material/dialog';
import { VisibilityInfoDialogComponent } from './visibility-info-dialog/visibility-info-dialog.component';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MeetingListService } from '../../meeting-list.service';
import { take } from 'rxjs';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';

@Component({
  selector: 'meeting-analysis-meeting-list-item',
  templateUrl: './meeting-list-item.component.html',
  styleUrls: ['./meeting-list-item.component.scss'],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    TranslateModule,
    DatePipe,
    SourcePipe,
    StartAndEndComponent,
    GuestsComponent,
    ConversationPagePathPipe,
    GalaxyFormFieldModule,
    GalaxyFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxySnackbarModule,
  ],
})
export class MeetingListItemComponent {
  constructor(
    private readonly dialog: MatDialog,
    private readonly meetingListService: MeetingListService,
    private readonly snackbarService: SnackbarService,
  ) {
    effect(() => {
      this.conversationPrivacyLevel.set(this.conversation().privacyLevel);
    });
  }

  conversation = input.required<Conversation>();

  meetingLink = computed(() => {
    const conversation = this.conversation();
    if (conversation.source === ConversationSource.CONVERSATION_SOURCE_GOOGLE_MEET) {
      return `https://meet.google.com/${conversation.externalId}`;
    } else if (conversation.source === ConversationSource.CONVERSATION_SOURCE_TEAMS) {
      return `https://teams.microsoft.com/l/meetup-join/${conversation.externalId}`;
    }
    return '';
  });

  internalAttendees = computed(() => {
    const attendees = this.conversation()?.internalAttendees || [];
    return [...new Set(attendees)];
  });
  externalAttendeesContactIds = computed(() => {
    const conversation = this.conversation();
    return (
      (conversation?.externalAttendees || [])
        .map((attendee) => attendee.contactId)
        .filter((contactId) => !!contactId) || []
    );
  });

  readonly namespaceName$ = inject(MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$);

  stopPropagation(e: Event): void {
    // used to prevent event propagation for anchor tag
    e.stopPropagation();
  }

  openVisibilityInfoDialog(): void {
    this.dialog.open(VisibilityInfoDialogComponent, {
      width: '500px',
    });
  }

  conversationPrivacyLevel: WritableSignal<PrivacyLevel | null> = signal(null);
  updatingPrivacyLevel = signal(false);
  privacyLevels = PrivacyLevel;

  changePrivacyLevel(privacyLevel: PrivacyLevel): void {
    this.updatingPrivacyLevel.set(true);
    const curConv = this.conversation();
    this.meetingListService
      .updateConversationVisibility(curConv.id, curConv.namespace, privacyLevel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('VISIBILITY.SUCCESS_CHANGING');
          this.conversationPrivacyLevel.set(privacyLevel);
          this.updatingPrivacyLevel.set(false);
        },
        error: () => {
          this.snackbarService.openErrorSnack('VISIBILITY.ERROR_CHANGING');
          this.updatingPrivacyLevel.set(false);
        },
      });
    return;
  }
}
